import { useAppContext } from "@/runtime/adapters/context/Context";
import {
  getWidgetPreviewSettings,
  subscribeWidgetPreviewSettings,
} from "@/runtime/adapters/widgetPreview";
import { useBeeOptions } from "@/shared/adapters/api/bee.api";
import { addPageScript } from "@/shared/adapters/dom";
import {
  prepareAdditionalCss,
  prepareBaseColors,
} from "@/shared/ui/Theme/reset.theme";
import { useEffect, useSyncExternalStore } from "react";
import { generatePrimaryPaletteAdaptive } from "../../../../../shared/theme/primaryPalette";
export const CustomStyles = () => {
  const preview = useSyncExternalStore(
    subscribeWidgetPreviewSettings,
    getWidgetPreviewSettings,
    () => undefined,
  );
  const { demoPage, noAdditionalCSS } = useAppContext().config;
  const {
    additionalCss = "",
    primaryColor = "",
    primaryColorContrast = "",
    secondaryColor = "",
    secondaryColorContrast = "",
  } = useBeeOptions((options) => ({
    additionalCss: options?.additionalCss,
    primaryColor: options?.primaryColor,
    secondaryColor: options?.secondaryColor,
    primaryColorContrast: options?.primaryColorContrast,
    secondaryColorContrast: options?.secondaryColorContrast,
  }))!;

  useEffect(() => {
    if (
      !primaryColor &&
      !secondaryColor &&
      !primaryColorContrast &&
      !secondaryColorContrast &&
      !additionalCss
    )
      return;

    // Demo and administrator controls use the same palette calculation.
    let demoColors = "";
    if (demoPage) {
      const seed = primaryColor || "#5263be";
      const light = generatePrimaryPaletteAdaptive(
        seed,
        "light",
        "#ffffff",
        "#f0f0f2",
      );
      const dark = generatePrimaryPaletteAdaptive(
        seed,
        "dark",
        "#252429",
        "#1a191e",
      );
      demoColors = `
        .demo-page[data-demo-theme="light"] {
          --demo-primary: ${light.primary50};
          --demo-action-bg: ${light.primary50};
          --demo-action-text: ${light.primary100};
        }
        .demo-page[data-demo-theme="dark"] {
          --demo-primary: color-mix(in srgb, ${dark.primary80}, #fff 30%);
          --demo-primary-hover: color-mix(in srgb, ${dark.primary80}, #fff 45%);
          --demo-action-bg: ${dark.primary60};
          --demo-action-text: ${dark.primary99};
        }
      `;
    }
    const css = [
      prepareBaseColors({
        primaryColor,
        primaryColorContrast,
        secondaryColor,
        secondaryColorContrast,
      }),
      noAdditionalCSS || preview?.customCss === false
        ? ""
        : prepareAdditionalCss(additionalCss),
      demoColors,
    ]
      .join("\n\n")
      .trim();

    // addPageScript intentionally inserts once on customer websites. A preview
    // owns its style element and must also be able to remove previously applied
    // custom CSS when the live setting is disabled.
    const previewStyle = preview
      ? document.getElementById("websail-styles")
      : null;
    if (previewStyle) previewStyle.textContent = css;
    else addPageScript(css, "style");
  }, [
    demoPage,
    noAdditionalCSS,
    preview?.customCss,
    additionalCss,
    primaryColor,
    primaryColorContrast,
    secondaryColor,
    secondaryColorContrast,
  ]);

  return null;
};
